import {ChangeEvent, FormEvent, useEffect, useRef, useState} from 'react';
import {loadOrderDocuments, OrderDocumentMetadata, uploadOrderDocument} from './api';
import {CUSTOMER_DOCUMENT_ALLOWED_MEDIA_TYPES, CUSTOMER_DOCUMENT_MAX_BYTES} from './CustomerDocumentUploadAdapter';

type RetryableUpload = {file: File; idempotencyKey: string};
// File objects read from the same untouched <input type="file"> across repeated submits are not
// guaranteed to be the same reference in every browser/test environment, so compare identity by
// the fields that actually distinguish a genuinely different selection.
function sameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

async function loadReadableDocuments(orderId: string): Promise<OrderDocumentMetadata[]> {
  const items = await loadOrderDocuments(orderId);
  if (!items.every(item => item && typeof item.document_id === 'string'
    && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(item.document_id)
    && typeof item.filename === 'string' && item.filename.length > 0
    && Number.isSafeInteger(item.byte_size) && item.byte_size > 0
    && ['quarantined', 'validating', 'valid', 'rejected', 'failed'].includes(item.validation_state))
    || new Set(items.map(item => item.document_id)).size !== items.length) {
    throw new Error('Document list could not be confirmed.');
  }
  return items;
}

export function OrderDocuments({orderId}: {orderId: string}) {
  const [items, setItems] = useState<OrderDocumentMetadata[] | null>(null);
  const [status, setStatus] = useState('Loading documents…');
  const [pending, setPending] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [retryable, setRetryable] = useState<RetryableUpload | null>(null);
  const [reload, setReload] = useState(0);
  // Issue #192 founder-reported post-live finding (2026-10-04): this form used to render
  // unconditionally, which read as a second, competing "upload a document" area alongside
  // CompanyDocumentIntake's guided "Start with the court papers" flow mounted on the same screen
  // (AttorneyIntake.tsx) -- both end up calling the same uploadOrderDocument(), so neither is
  // wrong, but showing both open at once is confusing. Collapsed by default; "Add another
  // document" still reaches this form for a non-PDF file or an additional exhibit.
  const [showUploadForm, setShowUploadForm] = useState(false);
  const busy = useRef(true);
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    let active = true;
    busy.current = true; setPending(true); setItems(null);
    loadReadableDocuments(orderId).then(next => {if (active) {setItems(next); setStatus(next.length ? 'Documents loaded.' : 'No documents uploaded.');}})
      .catch(() => {if (active) setStatus('Documents could not be loaded. Reload documents to check availability.');})
      .finally(() => {if (active) {busy.current = false; setPending(false);}});
    return () => {active = false;};
  }, [orderId, reload]);
  async function performUpload(file: File, idempotencyKey: string) {
    busy.current = true; setPending(true); setUploading(true); setRetryable(null);
    setStatus(`Uploading ${file.name}…`);
    let saved = false;
    try {
      const receipt = await uploadOrderDocument(orderId, file, idempotencyKey);
      // A successful HTTP response without a stored document identity is still an uncertain upload.
      // Keep the selected file and its retry key until a usable receipt confirms storage.
      if (typeof receipt.document_id !== 'string'
        || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt.document_id)) {
        throw new Error('Upload receipt could not be confirmed.');
      }
      saved = true;
      formRef.current?.reset();
      setItems(await loadReadableDocuments(orderId));
      setStatus('Document stored privately. Validation is pending; it is not available for extraction or download.');
    } catch {
      if (saved) {
        setItems(null);
        setStatus('Document was stored, but the list could not be refreshed. Reload documents.');
      } else {
        setStatus(`Upload of ${file.name} could not be confirmed. Retry this file, or reload documents and check the list before uploading again.`);
        setRetryable({file, idempotencyKey});
      }
    } finally {busy.current = false; setPending(false); setUploading(false);}
  }
  function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setRetryable(null);
    if (file) setStatus(`${file.name} selected. Ready to upload.`);
  }
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current || items === null) return;
    const file = (event.currentTarget.elements.namedItem('document') as HTMLInputElement).files?.[0];
    if (!file || file.size < 1 || file.size > CUSTOMER_DOCUMENT_MAX_BYTES || !CUSTOMER_DOCUMENT_ALLOWED_MEDIA_TYPES.some(type => type === file.type)) {
      setStatus('Choose a PDF, TIFF, PNG, or JPEG document up to 25 MB.'); return;
    }
    // If the same unconfirmed File from a failed attempt is still selected (the input was never
    // touched again), an ordinary Upload click must recover it exactly like Retry -- otherwise a
    // response lost after the original POST actually committed creates a second document, since
    // the backend dedupes by idempotency hash, not content hash (Theo STOP/CORRECT, PR #218).
    const key = retryable && sameFile(retryable.file, file) ? retryable.idempotencyKey : crypto.randomUUID();
    await performUpload(file, key);
  }
  async function retry() {
    if (busy.current || !retryable) return;
    await performUpload(retryable.file, retryable.idempotencyKey);
  }
  return <section aria-labelledby="order-documents-title">
    <h3 id="order-documents-title">Documents for this request</h3>
    <p role="status" aria-live="polite">{status}</p>
    {uploading && <progress aria-label="Uploading document"/>}
    <button type="button" disabled={pending} onClick={() => setReload(value => value + 1)}>Reload documents</button>
    {retryable && <button type="button" disabled={pending} onClick={() => void retry()}>Retry this file</button>}
    {items && items.length > 0 && <ul>{items.map(item => <li key={item.document_id}><strong>{item.filename}</strong> · {item.byte_size.toLocaleString()} bytes · {item.validation_state === 'quarantined' ? 'Stored privately — validation pending' : item.validation_state}</li>)}</ul>}
    {!showUploadForm && <button type="button" disabled={pending} onClick={() => setShowUploadForm(true)}>Add another document</button>}
    {showUploadForm && <>
      <p>PDF, TIFF, PNG, or JPEG, up to 25 MB each. Uploads remain private and quarantined until validation. Extraction has not run.</p>
      <form ref={formRef} onSubmit={event => void upload(event)}><fieldset disabled={pending || items === null}>
        <legend>Upload a document</legend>
        <label>Document file<input name="document" type="file" required accept={CUSTOMER_DOCUMENT_ALLOWED_MEDIA_TYPES.join(',')} onChange={selectFile}/></label>
        <button type="submit">Upload to private storage</button>
      </fieldset></form>
    </>}
  </section>;
}
